import { AtlasMapValue } from 'game-ui-types'
import { getGameAssetsBasePath, withCacheTimestamp } from './gameSettings'

interface TexturePackerFrame {
    frame: {
        x: number
        y: number
        w: number
        h: number
    }
    rotated?: boolean
}

export interface TexturePackerAtlas {
    frames: Record<string, TexturePackerFrame>
    meta?: {
        image?: string
        size?: {
            w: number
            h: number
        }
    }
}

const isExplicitAtlasPath = (atlasPath: string) =>
    /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(atlasPath) || atlasPath.startsWith('/') || atlasPath.startsWith('.')

export const getAtlasJsonUrl = (atlasPath: string, basePath?: string): string | undefined => {
    const trimmedPath = atlasPath.trim()

    if (!trimmedPath) {
        return
    }

    const resolvedBasePath =
        basePath ?? (isExplicitAtlasPath(trimmedPath) ? window.location.href : getGameAssetsBasePath())
    const atlasUrl = new URL(trimmedPath, resolvedBasePath)

    if (/\.(png|jpe?g|webp)$/i.test(atlasUrl.pathname)) {
        atlasUrl.pathname = atlasUrl.pathname.replace(/\.(png|jpe?g|webp)$/i, '.json')
    } else if (!/\.json$/i.test(atlasUrl.pathname)) {
        atlasUrl.pathname = `${atlasUrl.pathname}.json`
    }

    return withCacheTimestamp(atlasUrl.toString())
}

export const getFallbackAtlasImageName = (atlasJsonUrl: string) => {
    const jsonUrl = new URL(atlasJsonUrl)
    const jsonName = jsonUrl.pathname.split('/').pop() ?? ''

    return jsonName.replace(/\.json$/i, '.png')
}

// The engine's KTX2 pass rewrites `meta.image` to the GPU-compressed `.basis.ktx2` texture, which the
// browser can't render in <img>/CSS. The original PNG is still shipped next to it, so use that instead.
export const getAtlasImageName = (atlas: TexturePackerAtlas, atlasJsonUrl: string) => {
    const imageName = atlas.meta?.image

    if (!imageName || /\.(ktx2|basis)$/i.test(imageName)) {
        return getFallbackAtlasImageName(atlasJsonUrl)
    }

    return imageName
}

export const getAtlasImageUrl = (imageName: string, atlasJsonUrl: string) => {
    const jsonUrl = new URL(atlasJsonUrl)
    const imageUrl = new URL(imageName, jsonUrl)

    if (!imageUrl.search) {
        imageUrl.search = jsonUrl.search
    }

    return imageUrl.toString()
}

export const toAtlasMapValues = (atlas: TexturePackerAtlas): AtlasMapValue[] =>
    Object.entries(atlas.frames).map(([name, data]) => ({
        name,
        x: data.frame.x,
        y: data.frame.y,
        width: data.frame.w,
        height: data.frame.h,
        rotate: data.rotated ? 1 : 0,
    }))

export const isObjectUrl = (url: string) => url.startsWith('blob:')
